import React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import {
  applyPayloadToSchemaText,
  buildExportPayload,
  parseQuestionsJson,
  parseQuestionsTxt,
  serializeQuestionsJson,
  serializeQuestionsTxt,
} from '../lib/counselSurveyQuestionsBulk'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'

function unwrap(res) {
  return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
}

const QUESTION_TYPES = [
  { value: 'text', label: '텍스트' },
  { value: 'long-text', label: '긴 텍스트' },
  { value: 'tel', label: '전화' },
  { value: 'date', label: '날짜' },
  { value: 'single-select', label: '단일 선택' },
  { value: 'multi-select', label: '복수 선택' },
  { value: 'select', label: '목록 선택' },
]

function isOptionQuestionType(t) {
  const x = String(t || '').toLowerCase()
  return ['single-select', 'multi-select', 'select'].includes(x)
}

function parseSchemaSafe(text) {
  try {
    return JSON.parse(text || '{}')
  } catch {
    return null
  }
}

function deepClone(value) {
  try {
    return JSON.parse(JSON.stringify(value || {}))
  } catch {
    return {}
  }
}

/** 스키마 텍스트에 questionBank만 반영해 문자열로 반환 */
function applyQuestionBankToSchemaText(schemaText, questionBank) {
  const s = parseSchemaSafe(schemaText)
  if (!s || typeof s !== 'object') return schemaText
  const next = deepClone(s)
  next.questionBank = Array.isArray(questionBank) ? questionBank : []
  return JSON.stringify(next, null, 2)
}

function normalizeBankEntry(raw, rowIndex = 0) {
  if (!raw || typeof raw !== 'object') return null
  const q = raw.question && typeof raw.question === 'object' ? deepClone(raw.question) : {}
  if (!String(q.key || '').trim()) {
    q.key = `qb_row_${rowIndex}_${Date.now().toString(36)}`
  }
  return {
    sectionKey: raw.sectionKey != null ? String(raw.sectionKey) : '',
    sectionTitle: raw.sectionTitle != null ? String(raw.sectionTitle) : '',
    question: q,
  }
}

function optionsLinesFromQuestion(q) {
  const opts = Array.isArray(q?.options) ? q.options : []
  return opts.map((o) => {
    if (typeof o === 'string') return o
    const v = o?.value != null ? String(o.value) : ''
    const lab = o?.label != null ? String(o.label) : ''
    if (v && lab && v !== lab) return `${v}|${lab}`
    return lab || v || ''
  }).join('\n')
}

function parseOptionsLines(text) {
  const lines = String(text || '').split('\n').map((l) => l.trim()).filter(Boolean)
  return lines.map((line) => {
    const pipe = line.indexOf('|')
    if (pipe > 0) {
      const value = line.slice(0, pipe).trim()
      const label = line.slice(pipe + 1).trim()
      return { value: value || label, label: label || value }
    }
    return { value: line, label: line }
  })
}

function emptyBankQuestion(type = 'single-select') {
  const q = {
    key: `qb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    type,
    label: '',
    required: false,
  }
  if (isOptionQuestionType(type)) {
    q.options = [
      { value: '보기1', label: '보기1' },
      { value: '보기2', label: '보기2' },
    ]
  }
  return q
}

function getDefaultSchema(category) {
  const title = `${category?.name || '고객'} 설문`
  return {
    title,
    description: category?.description || '고객 상담 전 확인이 필요한 핵심 문항입니다.',
    sections: [
      {
        key: 'basic',
        title: '기본 정보',
        questions: [
          {
            key: 'jobType',
            type: 'single-select',
            label: '현재 직업 및 소득 형태를 알려주세요.',
            required: true,
            options: [
              { value: 'employee', label: '직장인' },
              { value: 'business', label: '자영업/사업자' },
              { value: 'freelancer', label: '프리랜서' },
            ],
          },
          {
            key: 'familyNeed',
            type: 'multi-select',
            label: '가장 관심 있는 상담 주제를 선택해 주세요.',
            options: [
              { value: 'coverage', label: '보장 점검' },
              { value: 'premium', label: '보험료 조정' },
              { value: 'family', label: '가족 보장' },
            ],
          },
        ],
      },
      {
        key: 'memo',
        title: '추가 메모',
        questions: [
          {
            key: 'memo',
            type: 'long-text',
            label: '추가로 확인하고 싶은 내용을 적어주세요.',
          },
        ],
      },
    ],
  }
}

function QuestionBankPanel({ schemaText, entries, onApplyEntries }) {
  const schema = parseSchemaSafe(schemaText) || {}
  const sections = Array.isArray(schema.sections) ? schema.sections : []

  const pickSection = (key) => {
    const s = sections.find((x) => String(x?.key || '') === String(key))
    return s || null
  }

  const patchEntry = (index, fn) => {
    const next = entries.map((e, i) => (i === index ? fn(e) : e))
    onApplyEntries(next)
  }

  const addEntry = () => {
    const first = sections[0]
    onApplyEntries([
      ...entries,
      {
        sectionKey: first?.key != null ? String(first.key) : 'basic',
        sectionTitle: first?.title || first?.key || '기본',
        question: emptyBankQuestion('single-select'),
      },
    ])
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-800">추천 문항 (questionBank)</div>
          <p className="mt-0.5 text-xs text-slate-500">
            본문 섹션에 넣지 않은 선택 문항입니다. 설문 수정 STEP·구역(<span className="font-mono">sectionKey</span>)과 맞추면 사용자 화면의 「문항 은행」에 표시됩니다.
          </p>
        </div>
        <button
          type="button"
          onClick={addEntry}
          className="shrink-0 rounded-lg bg-teal-700 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-teal-800"
        >
          + 추천 문항 추가
        </button>
      </div>

      {sections.length === 0 && (
        <div className="mt-2 rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2 text-xs text-amber-900">
          스키마에 <span className="font-mono">sections</span>가 없습니다. 아래 JSON에서 섹션을 정의한 뒤 추천 문항을 연결하세요.
        </div>
      )}

      <div className="mt-3 max-h-[min(22rem,55vh)] space-y-3 overflow-y-auto pr-0.5">
        {entries.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-200 bg-white px-3 py-6 text-center text-xs text-slate-500">
            등록된 추천 문항이 없습니다.
          </div>
        )}
        {entries.map((entry, index) => {
          const q = entry.question || {}
          const t = String(q.type || 'text').toLowerCase()
          const sectionSelectValue = sections.some((s) => String(s.key) === String(entry.sectionKey))
            ? String(entry.sectionKey)
            : '__custom__'
          const optType = isOptionQuestionType(t)

          return (
            <div key={`${q.key}-${index}`} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2">
                <span className="text-xs font-semibold text-slate-600">#{index + 1}</span>
                <button
                  type="button"
                  className="text-xs font-medium text-red-700 hover:underline"
                  onClick={() => onApplyEntries(entries.filter((_, i) => i !== index))}
                >
                  이 추천 삭제
                </button>
              </div>

              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-slate-600">연결 섹션</label>
                  <select
                    className="mt-0.5 w-full rounded border px-2 py-1.5 text-sm"
                    value={sectionSelectValue}
                    onChange={(e) => {
                      const v = e.target.value
                      if (v === '__custom__') {
                        patchEntry(index, (prev) => ({ ...prev, sectionKey: '', sectionTitle: '' }))
                        return
                      }
                      const sec = pickSection(v)
                      patchEntry(index, (prev) => ({
                        ...prev,
                        sectionKey: v,
                        sectionTitle: sec?.title || sec?.key || prev.sectionTitle,
                      }))
                    }}
                  >
                    <option value="__custom__">직접 입력 (키·제목)</option>
                    {sections.map((s) => (
                      <option key={String(s.key)} value={String(s.key)}>
                        {(s.title || s.key) + ` (${s.key})`}
                      </option>
                    ))}
                  </select>
                </div>
                {sectionSelectValue === '__custom__' && (
                  <>
                    <div>
                      <label className="text-xs font-medium text-slate-600">sectionKey</label>
                      <input
                        className="mt-0.5 w-full rounded border px-2 py-1.5 font-mono text-sm"
                        placeholder="예: basic, needs"
                        value={entry.sectionKey}
                        onChange={(e) => patchEntry(index, (prev) => ({ ...prev, sectionKey: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600">sectionTitle</label>
                      <input
                        className="mt-0.5 w-full rounded border px-2 py-1.5 text-sm"
                        placeholder="구역 표시 제목"
                        value={entry.sectionTitle}
                        onChange={(e) => patchEntry(index, (prev) => ({ ...prev, sectionTitle: e.target.value }))}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-medium text-slate-600">문항 ID (key)</label>
                  <input
                    className="mt-0.5 w-full rounded border px-2 py-1.5 font-mono text-sm"
                    value={q.key || ''}
                    onChange={(e) => patchEntry(index, (prev) => ({
                      ...prev,
                      question: { ...deepClone(prev.question), key: e.target.value },
                    }))}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">유형</label>
                  <select
                    className="mt-0.5 w-full rounded border px-2 py-1.5 text-sm"
                    value={QUESTION_TYPES.some((o) => o.value === t) ? t : 'text'}
                    onChange={(e) => {
                      const nt = e.target.value
                      patchEntry(index, (prev) => {
                        const nq = { ...deepClone(prev.question), type: nt }
                        if (isOptionQuestionType(nt)) {
                          if (!Array.isArray(nq.options) || nq.options.length === 0) {
                            nq.options = [
                              { value: '보기1', label: '보기1' },
                              { value: '보기2', label: '보기2' },
                            ]
                          }
                        } else {
                          delete nq.options
                        }
                        return { ...prev, question: nq }
                      })
                    }}
                  >
                    {QUESTION_TYPES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-slate-600">질문 문구 (label)</label>
                  <input
                    className="mt-0.5 w-full rounded border px-2 py-1.5 text-sm"
                    value={q.label || ''}
                    onChange={(e) => patchEntry(index, (prev) => ({
                      ...prev,
                      question: { ...deepClone(prev.question), label: e.target.value },
                    }))}
                  />
                </div>
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={!!q.required}
                    onChange={(e) => patchEntry(index, (prev) => ({
                      ...prev,
                      question: { ...deepClone(prev.question), required: e.target.checked },
                    }))}
                  />
                  필수
                </label>
              </div>

              {optType && (
                <div className="mt-2">
                  <label className="text-xs font-medium text-slate-600">선택지 (줄당 하나, 선택적으로 value|label)</label>
                  <textarea
                    className="mt-0.5 min-h-[72px] w-full rounded border px-2 py-1.5 font-mono text-xs"
                    placeholder={'예:\n건강\n사고\n가족\n또는\nA|건강\nB|사고'}
                    value={optionsLinesFromQuestion(q)}
                    onChange={(e) => {
                      const opts = parseOptionsLines(e.target.value)
                      patchEntry(index, (prev) => ({
                        ...prev,
                        question: { ...deepClone(prev.question), options: opts },
                      }))
                    }}
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function CounselSurveyTemplateAdminPanel() {
  const [selectedCategoryCode, setSelectedCategoryCode] = React.useState('ALL')
  const [selectedTemplateId, setSelectedTemplateId] = React.useState(null)
  const [categoryForm, setCategoryForm] = React.useState({
    id: null,
    code: '',
    name: '',
    description: '',
    displayOrder: 0,
    active: true,
  })
  const [templateForm, setTemplateForm] = React.useState({
    id: null,
    name: '',
    categoryCode: 'COMMON',
    description: '',
    status: 'PUBLISHED',
    publish: true,
    schemaText: '',
  })
  /** 운영 추천 문항 questionBank — 저장 시 schema에 병합 */
  const [questionBankEntries, setQuestionBankEntries] = React.useState([])
  const [bulkExportFormat, setBulkExportFormat] = React.useState('json')
  const bulkFileInputRef = React.useRef(null)

  const applyQuestionBankEntries = React.useCallback((next) => {
    setQuestionBankEntries(next)
    setTemplateForm((tf) => ({
      ...tf,
      schemaText: applyQuestionBankToSchemaText(tf.schemaText, next),
    }))
  }, [])

  const categoriesQuery = useQuery({
    queryKey: ['counsel-surveys', 'categories', 'admin'],
    queryFn: async () => unwrap(await api.get('/counsel-surveys/categories', { params: { activeOnly: false }, __silent: true })),
  })

  const templatesQuery = useQuery({
    queryKey: ['counsel-surveys', 'templates', 'admin', selectedCategoryCode],
    queryFn: async () => unwrap(await api.get('/counsel-surveys/templates', {
      params: selectedCategoryCode !== 'ALL' ? { categoryCode: selectedCategoryCode } : {},
      __silent: true,
    })),
  })

  const templateDetailQuery = useQuery({
    queryKey: ['counsel-surveys', 'template-detail', selectedTemplateId],
    enabled: !!selectedTemplateId,
    queryFn: async () => unwrap(await api.get(`/counsel-surveys/templates/${selectedTemplateId}`, { __silent: true })),
  })

  React.useEffect(() => {
    const categories = Array.isArray(categoriesQuery.data) ? categoriesQuery.data : []
    if (selectedCategoryCode === 'ALL' && categories.length > 0) {
      setCategoryForm(prev => prev.id ? prev : {
        id: categories[0].id,
        code: categories[0].code,
        name: categories[0].name,
        description: categories[0].description || '',
        displayOrder: categories[0].displayOrder || 0,
        active: categories[0].active !== false,
      })
    }
  }, [categoriesQuery.data, selectedCategoryCode])

  React.useEffect(() => {
    const categories = Array.isArray(categoriesQuery.data) ? categoriesQuery.data : []
    if (!selectedCategoryCode || selectedCategoryCode === 'ALL') return
    const selected = categories.find(item => item.code === selectedCategoryCode)
    if (selected) {
      setCategoryForm({
        id: selected.id,
        code: selected.code,
        name: selected.name,
        description: selected.description || '',
        displayOrder: selected.displayOrder || 0,
        active: selected.active !== false,
      })
    }
  }, [categoriesQuery.data, selectedCategoryCode])

  React.useEffect(() => {
    if (!templateDetailQuery.data) {
      return
    }
    const item = templateDetailQuery.data
    const schema = item.schema && typeof item.schema === 'object' ? item.schema : {}
    const bank = Array.isArray(schema.questionBank) ? schema.questionBank : []
    setQuestionBankEntries(
      bank
        .map((e, i) => normalizeBankEntry(e, i))
        .filter(Boolean),
    )
    setTemplateForm({
      id: item.id,
      name: item.name || '',
      categoryCode: item.categoryCode || 'COMMON',
      description: item.description || '',
      status: item.status || 'PUBLISHED',
      publish: item.status === 'PUBLISHED',
      schemaText: JSON.stringify(item.schema || {}, null, 2),
    })
  }, [templateDetailQuery.data])

  const refresh = async () => {
    await Promise.all([categoriesQuery.refetch(), templatesQuery.refetch(), templateDetailQuery.refetch()])
  }

  const saveCategoryMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        code: categoryForm.code,
        name: categoryForm.name,
        description: categoryForm.description,
        displayOrder: Number(categoryForm.displayOrder) || 0,
        active: categoryForm.active,
      }
      if (categoryForm.id) {
        return unwrap(await api.patch(`/counsel-surveys/categories/${categoryForm.id}`, payload))
      }
      return unwrap(await api.post('/counsel-surveys/categories', payload))
    },
    onSuccess: async (data) => {
      try { window?.toast?.show?.('카테고리를 저장했습니다.', 'success') } catch {}
      setSelectedCategoryCode(data.code)
      await refresh()
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '카테고리 저장 실패', 'error') } catch {}
    },
  })

  const saveTemplateMutation = useMutation({
    mutationFn: async () => {
      const base = parseSchemaSafe(templateForm.schemaText)
      if (base == null) {
        throw new Error('스키마 JSON 형식이 올바르지 않습니다.')
      }
      const schema = deepClone(base)
      schema.questionBank = questionBankEntries
      const payload = {
        name: templateForm.name,
        categoryCode: templateForm.categoryCode,
        description: templateForm.description,
        templateScope: templateForm.categoryCode === 'COMMON' ? 'COMMON' : 'CATEGORY',
        publish: templateForm.publish,
        status: templateForm.status,
        schema,
      }
      if (templateForm.id) {
        return unwrap(await api.patch(`/counsel-surveys/templates/${templateForm.id}`, payload))
      }
      return unwrap(await api.post('/counsel-surveys/templates', payload))
    },
    onSuccess: async (data) => {
      try { window?.toast?.show?.('고객설문 템플릿을 저장했습니다.', 'success') } catch {}
      setSelectedTemplateId(data.id)
      await refresh()
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || e?.message || '템플릿 저장 실패'
      try { window?.toast?.show?.(msg, 'error') } catch {}
    },
  })

  const bulkImportMutation = useMutation({
    mutationFn: async ({ text, fileName }) => {
      const lower = String(fileName || '').toLowerCase()
      const trimmed = String(text || '').replace(/^\uFEFF/, '').trimStart()
      const tryJson = lower.endsWith('.json') || trimmed.startsWith('{')
      const payload = tryJson ? parseQuestionsJson(text) : parseQuestionsTxt(text)
      const schemaTextNext = applyPayloadToSchemaText(templateForm.schemaText, payload)
      const entries = (payload.questionBank || [])
        .map((e, i) => normalizeBankEntry(e, i))
        .filter(Boolean)
      const merged = parseSchemaSafe(schemaTextNext)
      if (!merged) throw new Error('병합된 스키마를 만들 수 없습니다.')
      if (!templateForm.id) {
        return { localOnly: true, schemaTextNext, entries }
      }
      const apiPayload = {
        name: templateForm.name,
        categoryCode: templateForm.categoryCode,
        description: templateForm.description,
        templateScope: templateForm.categoryCode === 'COMMON' ? 'COMMON' : 'CATEGORY',
        publish: templateForm.publish,
        status: templateForm.status,
        schema: { ...merged, sections: payload.sections, questionBank: payload.questionBank },
      }
      unwrap(await api.patch(`/counsel-surveys/templates/${templateForm.id}`, apiPayload))
      return { localOnly: false, schemaTextNext, entries }
    },
    onSuccess: async (result) => {
      setQuestionBankEntries(result.entries)
      setTemplateForm((tf) => ({ ...tf, schemaText: result.schemaTextNext }))
      if (!result.localOnly) {
        try { window?.toast?.show?.('문항을 반영해 서버에 갱신했습니다.', 'success') } catch {}
        await refresh()
      } else {
        try { window?.toast?.show?.('문항을 불러왔습니다. 서버에 올리려면 「템플릿 저장」을 눌러 주세요.', 'success') } catch {}
      }
    },
    onError: (e) => {
      const msg = e?.message || e?.response?.data?.message || '문항 가져오기 실패'
      try { window?.toast?.show?.(msg, 'error') } catch {}
    },
  })

  const downloadQuestionsBulk = () => {
    try {
      const payload = buildExportPayload(templateForm.schemaText, questionBankEntries)
      const body = bulkExportFormat === 'json'
        ? serializeQuestionsJson(payload)
        : serializeQuestionsTxt(payload, templateForm.name)
      const ext = bulkExportFormat === 'json' ? 'json' : 'txt'
      const mime = bulkExportFormat === 'json' ? 'application/json;charset=utf-8' : 'text/plain;charset=utf-8'
      const safeName = String(templateForm.name || 'template').replace(/[/\\?%*:|"<>]/g, '-').slice(0, 80) || 'template'
      const idPart = templateForm.id || 'draft'
      const blob = new Blob([body], { type: mime })
      const a = document.createElement('a')
      const url = URL.createObjectURL(blob)
      a.href = url
      a.download = `counsel-survey-${idPart}-${safeName}-questions.${ext}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      try { window?.toast?.show?.(e?.message || '다운로드 실패', 'error') } catch {}
    }
  }

  if (categoriesQuery.isLoading || templatesQuery.isLoading) return <Loading />
  if (categoriesQuery.isError) return <InlineError message={categoriesQuery.error?.message || '카테고리를 불러오지 못했습니다.'} />
  if (templatesQuery.isError) return <InlineError message={templatesQuery.error?.message || '템플릿을 불러오지 못했습니다.'} />

  const categories = Array.isArray(categoriesQuery.data) ? categoriesQuery.data : []
  const templates = Array.isArray(templatesQuery.data) ? templatesQuery.data : []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">고객설문 템플릿</h2>
          <div className="mt-1 text-sm text-slate-500">카테고리를 운영자가 직접 관리하고, 카테고리별 기본 설문 템플릿을 배포합니다.</div>
        </div>
        <button type="button" onClick={refresh} className="rounded border px-3 py-2 text-sm hover:bg-slate-50">새로고침</button>
      </div>

      <div className="grid gap-4 xl:grid-cols-[0.9fr_1fr_1.2fr]">
        <section className="rounded-2xl border bg-white p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">카테고리 관리</h3>
            <button
              type="button"
              onClick={() => setCategoryForm({ id: null, code: '', name: '', description: '', displayOrder: 0, active: true })}
              className="rounded border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-800 hover:bg-teal-100"
            >
              새 카테고리
            </button>
          </div>
          <div className="mt-3 space-y-2">
            {categories.map(item => (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelectedCategoryCode(item.code)}
                className={`block w-full rounded-xl border p-3 text-left ${selectedCategoryCode === item.code ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'hover:bg-slate-50'}`}
              >
                <div className="font-medium">{item.name}</div>
                <div className="mt-1 text-xs text-slate-500">{item.code} · 순서 {item.displayOrder ?? 0}</div>
              </button>
            ))}
          </div>

          <div className="mt-4 space-y-3 border-t pt-4">
            <div>
              <label className="block text-sm font-semibold">코드</label>
              <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={categoryForm.code} disabled={!!categoryForm.id} onChange={(e) => setCategoryForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))} />
            </div>
            <div>
              <label className="block text-sm font-semibold">이름</label>
              <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={categoryForm.name} onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-semibold">설명</label>
              <textarea className="mt-1 min-h-[90px] w-full rounded border px-3 py-2 text-sm" value={categoryForm.description} onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-semibold">노출 순서</label>
              <input className="mt-1 w-full rounded border px-3 py-2 text-sm" type="number" value={categoryForm.displayOrder} onChange={(e) => setCategoryForm(prev => ({ ...prev, displayOrder: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={categoryForm.active} onChange={(e) => setCategoryForm(prev => ({ ...prev, active: e.target.checked }))} />
              활성 카테고리
            </label>
            <button
              type="button"
              onClick={() => saveCategoryMutation.mutate()}
              disabled={saveCategoryMutation.isPending}
              className="w-full rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
            >
              {saveCategoryMutation.isPending ? '저장 중...' : '카테고리 저장'}
            </button>
          </div>
        </section>

        <section className="rounded-2xl border bg-white p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">기본 템플릿 목록</h3>
            <div className="flex items-center gap-2">
              <select className="rounded border px-3 py-2 text-sm" value={selectedCategoryCode} onChange={(e) => setSelectedCategoryCode(e.target.value)}>
                <option value="ALL">전체</option>
                {categories.map(item => <option key={item.id} value={item.code}>{item.name}</option>)}
              </select>
              <button
                type="button"
                onClick={() => {
                  const cat = selectedCategoryCode === 'ALL'
                    ? categories[0]
                    : categories.find((item) => item.code === selectedCategoryCode)
                  setQuestionBankEntries([])
                  setSelectedTemplateId(null)
                  setTemplateForm({
                    id: null,
                    name: '',
                    categoryCode: selectedCategoryCode === 'ALL' ? (categories[0]?.code || 'COMMON') : selectedCategoryCode,
                    description: '',
                    status: 'PUBLISHED',
                    publish: true,
                    schemaText: JSON.stringify(getDefaultSchema(cat), null, 2),
                  })
                }}
                className="rounded border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100"
              >
                새 템플릿
              </button>
            </div>
          </div>

          <div className="mt-3 space-y-2">
            {templates.length === 0 && <div className="rounded-xl border border-dashed p-6 text-sm text-slate-400">선택한 카테고리의 고객설문 템플릿이 없습니다.</div>}
            {templates.map(item => (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelectedTemplateId(item.id)}
                className={`block w-full rounded-xl border p-3 text-left ${selectedTemplateId === item.id ? 'border-brand-sky bg-sky-50/40' : 'hover:bg-slate-50'}`}
              >
                <div className="font-medium">{item.name}</div>
                <div className="mt-1 text-xs text-slate-500">{item.categoryCode || 'COMMON'} · {item.status}</div>
              </button>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border bg-white p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{templateForm.id ? '고객설문 템플릿 수정' : '고객설문 템플릿 생성'}</h3>
            <button
              type="button"
              onClick={() => {
                setQuestionBankEntries([])
                setTemplateForm((prev) => ({
                  ...prev,
                  schemaText: JSON.stringify(getDefaultSchema(categories.find((item) => item.code === prev.categoryCode)), null, 2),
                }))
              }}
              className="rounded border px-3 py-1.5 text-xs hover:bg-slate-50"
            >
              기본 스키마
            </button>
          </div>
          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-sm font-semibold">템플릿명</label>
              <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={templateForm.name} onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-semibold">카테고리</label>
              <select className="mt-1 w-full rounded border px-3 py-2 text-sm" value={templateForm.categoryCode} onChange={(e) => setTemplateForm(prev => ({ ...prev, categoryCode: e.target.value }))}>
                {categories.map(item => <option key={item.id} value={item.code}>{item.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold">설명</label>
              <textarea className="mt-1 min-h-[90px] w-full rounded border px-3 py-2 text-sm" value={templateForm.description} onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={templateForm.publish} onChange={(e) => setTemplateForm(prev => ({ ...prev, publish: e.target.checked, status: e.target.checked ? 'PUBLISHED' : 'DRAFT' }))} />
              즉시 발행
            </label>

            <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3">
              <div className="text-sm font-semibold text-slate-800">문항 일괄 보내기 / 가져오기</div>
              <p className="mt-0.5 text-xs text-slate-500">
                본문 섹션(<span className="font-mono">sections</span>)과 추천 문항(<span className="font-mono">questionBank</span>)만 JSON 또는 TXT로 보냅니다. 가져오면 같은 항목이 덮어씌워지며, 이미 저장된 템플릿은 서버에 즉시 반영됩니다.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <label className="text-xs font-medium text-slate-600">보내기 형식</label>
                <select
                  className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm"
                  value={bulkExportFormat}
                  onChange={(e) => setBulkExportFormat(e.target.value)}
                >
                  <option value="json">JSON</option>
                  <option value="txt">TXT</option>
                </select>
                <button
                  type="button"
                  onClick={downloadQuestionsBulk}
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  문항 다운로드
                </button>
                <input
                  ref={bulkFileInputRef}
                  type="file"
                  accept=".json,.txt,application/json,text/plain"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0]
                    e.target.value = ''
                    if (!file) return
                    try {
                      const text = await file.text()
                      const ok = window.confirm(
                        '파일의 sections·questionBank로 현재 문항을 덮어씁니다. 계속할까요?',
                      )
                      if (!ok) return
                      bulkImportMutation.mutate({ text, fileName: file.name })
                    } catch (err) {
                      try { window?.toast?.show?.(err?.message || '파일을 읽지 못했습니다.', 'error') } catch {}
                    }
                  }}
                />
                <button
                  type="button"
                  disabled={bulkImportMutation.isPending}
                  onClick={() => bulkFileInputRef.current?.click()}
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  {bulkImportMutation.isPending ? '가져오는 중...' : '문항 업로드'}
                </button>
              </div>
            </div>

            <QuestionBankPanel
              schemaText={templateForm.schemaText}
              entries={questionBankEntries}
              onApplyEntries={applyQuestionBankEntries}
            />

            <div>
              <label className="block text-sm font-semibold">스키마 JSON</label>
              <p className="mt-0.5 text-xs text-slate-500">전체 스키마입니다. 저장 시 위 추천 문항이 <span className="font-mono">questionBank</span>로 합쳐집니다. JSON만 수정한 뒤 입력칸 밖을 클릭하면 추천 문항 패널과 동기화됩니다.</p>
              <textarea
                className="mt-1 min-h-[280px] w-full rounded border px-3 py-2 font-mono text-xs"
                value={templateForm.schemaText}
                onChange={(e) => setTemplateForm((prev) => ({ ...prev, schemaText: e.target.value }))}
                onBlur={(e) => {
                  const text = e.target.value
                  const s = parseSchemaSafe(text)
                  if (!s) return
                  const bank = Array.isArray(s.questionBank) ? s.questionBank : []
                  setQuestionBankEntries(bank.map((bk, i) => normalizeBankEntry(bk, i)).filter(Boolean))
                }}
              />
            </div>
            <button
              type="button"
              onClick={() => saveTemplateMutation.mutate()}
              disabled={saveTemplateMutation.isPending}
              className="w-full rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
            >
              {saveTemplateMutation.isPending ? '저장 중...' : '템플릿 저장'}
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}
